import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Clock } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type {
  SalesCheckStage,
  SalesCheckSummary,
  SalesDocumentSummary,
} from '@aeris/contracts'
import { Badge, Button, Field, Input, Section } from '@aeris/ui'

import { correctSalesCheck, recordSalesCheck } from '../lib/api/sales.ts'
import { ApiError } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * A conferência da venda — estoque e caixa (26/09).
 *
 * O dono: o estoquista pega a peça, confere e escrevia a hora na nota; a caixa
 * confere de novo e escrevia a dela; o papel ficava 30 dias guardado para,
 * quando o cliente diz que a peça não saiu, achar a hora e puxar a câmera.
 * Aqui o clique grava a hora do servidor e o nome de quem conferiu. Só o
 * gerente corrige, com motivo, e a hora que o sistema viu continua à vista.
 */

const CHECKABLE = ['confirmed', 'fulfilled', 'completed'] as const

const STAGE_LABEL: Record<SalesCheckStage, string> = {
  stock: 'Estoque',
  cashier: 'Caixa',
}

const dayAndTime = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})
const timeOnly = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
})

/** `19:12` — a hora do selo na lista. */
const checkTime = (iso: string): string => timeOnly.format(new Date(iso))

/** O valor de um `datetime-local` na hora deste computador. */
const localInputValue = (at: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${String(at.getFullYear())}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`
}

const isCheckable = (
  document: Pick<SalesDocumentSummary, 'kind' | 'status'>
): boolean =>
  document.kind !== 'quote' &&
  (CHECKABLE as readonly string[]).includes(document.status)

/**
 * O selo da lista: de relance, o que já foi conferido e o que falta.
 * Orçamento, rascunho e cancelada não se conferem — um traço.
 */
export const ChecksBadges = ({
  document,
}: {
  document: Pick<SalesDocumentSummary, 'kind' | 'status' | 'checks'>
}): ReactNode => {
  const { stock, cashier } = document.checks
  if (!isCheckable(document) && stock === null && cashier === null) {
    return <span className="text-(--text-muted)">—</span>
  }
  return (
    <span className="flex flex-wrap gap-1">
      {(['stock', 'cashier'] as const).map((stage) => {
        const check = document.checks[stage]
        return (
          <Badge key={stage} tone={check === null ? 'neutral' : 'success'}>
            {check === null
              ? `Falta ${STAGE_LABEL[stage].toLowerCase()}`
              : `${STAGE_LABEL[stage]} ${checkTime(check.checkedAt)}`}
          </Badge>
        )
      })}
    </span>
  )
}

const messageOf = (cause: unknown, fallback: string): string =>
  cause instanceof ApiError ? cause.message : fallback

const CheckLine = ({
  stage,
  check,
}: {
  stage: SalesCheckStage
  check: SalesCheckSummary | null
}): ReactNode => (
  <div className="flex flex-col gap-0.5">
    <p className="flex items-center gap-2 text-(length:--text-sm)">
      {check === null ? (
        <Clock aria-hidden="true" className="h-4 w-4 text-(--text-muted)" />
      ) : (
        <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-(--success)" />
      )}
      <span className="font-(--weight-semibold) text-(--text-primary)">
        {STAGE_LABEL[stage]}:
      </span>
      <span className="text-(--text-secondary)">
        {check === null
          ? 'aguardando'
          : `conferido ${dayAndTime.format(new Date(check.checkedAt))}${
              check.checkedByName === null ? '' : ` · ${check.checkedByName}`
            }`}
      </span>
    </p>
    {check?.correctedAt == null ? null : (
      <p className="ml-6 text-(length:--text-xs) text-(--text-muted)">
        Corrigido por {check.correctedByName ?? 'gerente'}:{' '}
        {check.correctionReason}
        {check.originalCheckedAt === null
          ? ' (registrada pelo gerente)'
          : ` (o sistema viu ${dayAndTime.format(new Date(check.originalCheckedAt))})`}
      </p>
    )}
  </div>
)

export const SalesChecksPanel = ({
  document,
}: {
  document: Pick<SalesDocumentSummary, 'id' | 'kind' | 'status' | 'checks'>
}): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [correcting, setCorrecting] = useState<SalesCheckStage | null>(null)
  const [when, setWhen] = useState('')
  const [reason, setReason] = useState('')

  const { stock, cashier } = document.checks
  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['sales-document', document.id],
    })
    await queryClient.invalidateQueries({ queryKey: ['sales-documents'] })
  }

  const record = useMutation({
    mutationFn: async (stage: SalesCheckStage) =>
      await recordSalesCheck(document.id, stage),
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: (cause: unknown) => {
      setError(messageOf(cause, 'Não foi possível gravar a conferência.'))
    },
  })

  const correct = useMutation({
    mutationFn: async (stage: SalesCheckStage) =>
      await correctSalesCheck(document.id, stage, {
        checkedAt: new Date(when).toISOString(),
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      setError(null)
      setCorrecting(null)
      await refresh()
    },
    onError: (cause: unknown) => {
      setError(messageOf(cause, 'Não foi possível corrigir a conferência.'))
    },
  })

  const checkable = isCheckable(document)
  if (!checkable && stock === null && cashier === null) return null

  const canCorrect = checkable && allows('sales.check.correct')
  const nextStage: SalesCheckStage | null = !checkable
    ? null
    : stock === null
      ? allows('sales.check.stock')
        ? 'stock'
        : null
      : cashier === null && allows('sales.check.cashier')
        ? 'cashier'
        : null

  const startCorrecting = (stage: SalesCheckStage) => {
    const current = document.checks[stage]
    setCorrecting(stage)
    setWhen(
      localInputValue(
        current === null ? new Date() : new Date(current.checkedAt)
      )
    )
    setReason('')
    setError(null)
  }

  return (
    <Section
      title="Conferência"
      description="A hora em que o estoque e o caixa conferiram a peça — para puxar a câmera se precisar."
    >
      <div className="flex flex-col gap-3">
        {(['stock', 'cashier'] as const).map((stage) => (
          <div
            key={stage}
            className="flex flex-wrap items-start justify-between gap-2"
          >
            <CheckLine stage={stage} check={document.checks[stage]} />
            {canCorrect && correcting !== stage ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  startCorrecting(stage)
                }}
              >
                {document.checks[stage] === null
                  ? `Registrar ${STAGE_LABEL[stage].toLowerCase()}`
                  : `Corrigir ${STAGE_LABEL[stage].toLowerCase()}`}
              </Button>
            ) : null}
          </div>
        ))}

        {correcting === null ? null : (
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Hora da conferência" className="w-56">
              {(field) => (
                <Input
                  {...field}
                  type="datetime-local"
                  value={when}
                  onChange={(event) => {
                    setWhen(event.target.value)
                  }}
                />
              )}
            </Field>
            <Field label="Motivo" className="min-w-48 flex-1">
              {(field) => (
                <Input
                  {...field}
                  maxLength={200}
                  placeholder="Ex.: clicou depois de entregar"
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value)
                  }}
                />
              )}
            </Field>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCorrecting(null)
                }}
              >
                Voltar
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={correct.isPending}
                disabled={reason.trim() === '' || when === ''}
                onClick={() => {
                  correct.mutate(correcting)
                }}
              >
                Salvar correção
              </Button>
            </div>
          </div>
        )}

        {nextStage === null ? null : (
          <Button
            variant="primary"
            className="w-full sm:w-auto"
            loading={record.isPending}
            leadingIcon={
              <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
            }
            onClick={() => {
              record.mutate(nextStage)
            }}
          >
            {`Conferi (${STAGE_LABEL[nextStage].toLowerCase()})`}
          </Button>
        )}

        {error === null ? null : (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {error}
          </p>
        )}
      </div>
    </Section>
  )
}
